import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PenLine, Loader2, Sparkles, Copy, Check } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

export default function CoverLetterGenerator() {
    const [form, setForm] = useState({ job_title: "", company: "", job_description: "", tone: "confident" });
    const [current, setCurrent] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const loadHistory = async () => {
        try {
            const { data } = await api.get("/professional/ai/cover-letter/history");
            setHistory(data);
        } catch (err) {
            // Non-critical background load — see ResumeBuilder for rationale.
            console.warn("[CoverLetterGenerator] loadHistory failed:", err?.message || err);
        }
    };
    useEffect(() => { loadHistory(); }, []);

    const generate = async () => {
        try {
            setLoading(true);
            const { data } = await api.post("/professional/ai/cover-letter", form);
            setCurrent(data);
            toast.success("Cover letter generated.");
            loadHistory();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    const copy = () => {
        if (!current?.markdown) return;
        navigator.clipboard.writeText(current.markdown);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div data-testid="cover-letter-page">
            <div className="mb-6">
                <p className="overline">AI Career Assistant</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Cover Letter Generator
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Paste a job description — Simonara AI writes a personalized letter grounded in your Passport.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
                <div className="card-soft p-5 h-fit lg:sticky lg:top-24 space-y-3">
                    <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Job title *" data-testid="cl-job-title" />
                    <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company (optional)" data-testid="cl-company" />
                    <Textarea rows={8} value={form.job_description} onChange={(e) => setForm({ ...form, job_description: e.target.value })} placeholder="Paste the job description *" data-testid="cl-job-desc" />
                    <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                        <SelectTrigger data-testid="cl-tone"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="confident">Confident</SelectItem>
                            <SelectItem value="warm">Warm</SelectItem>
                            <SelectItem value="concise">Concise</SelectItem>
                        </SelectContent>
                    </Select>
                    <button
                        onClick={generate}
                        disabled={loading || !form.job_title.trim() || !form.job_description.trim()}
                        className="btn-green w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        data-testid="cl-generate"
                    >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate cover letter
                    </button>

                    {history.length > 0 && (
                        <div className="pt-3 border-t border-border">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Recent letters</div>
                            <div className="space-y-1">
                                {history.map((h) => (
                                    <button key={h.id} onClick={() => setCurrent(h)} className={`w-full text-left rounded-lg p-2 text-xs border transition-colors ${current?.id === h.id ? "border-emerald-500/50 bg-emerald-500/5" : "border-border hover:border-emerald-500/30"}`} data-testid={`cl-history-${h.id}`}>
                                        <div className="text-foreground truncate">{h.job_title}{h.company ? ` · ${h.company}` : ""}</div>
                                        <div className="text-[10px] text-muted-foreground">{new Date(h.created_at).toLocaleString()}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    {!current ? (
                        <div className="card-soft p-12 text-center">
                            <PenLine size={22} className="mx-auto text-muted-foreground" />
                            <div className="mt-2 font-heading font-semibold">No letter yet.</div>
                            <div className="mt-1 text-sm text-muted-foreground">Paste a job description on the left and click Generate.</div>
                        </div>
                    ) : (
                        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                            <div className="card-soft p-5">
                                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                    <div>
                                        <div className="font-heading font-semibold text-foreground">Cover letter</div>
                                        <div className="text-[11px] text-muted-foreground mt-0.5">
                                            {current.job_title}{current.company ? ` · ${current.company}` : ""}
                                        </div>
                                    </div>
                                    <button onClick={copy} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="cl-copy">
                                        {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
                                    </button>
                                </div>
                                <Textarea readOnly value={current.markdown} rows={16} className="text-sm" data-testid="cl-markdown" />
                            </div>
                            {current.tone_analysis && (
                                <div className="card-soft p-4 border-emerald-500/30 bg-emerald-500/5">
                                    <div className="overline text-emerald-600">Tone analysis</div>
                                    <div className="mt-1 text-sm text-foreground">{current.tone_analysis}</div>
                                </div>
                            )}
                            {current.suggestions?.length > 0 && (
                                <div className="card-soft p-4">
                                    <div className="overline text-amber-600">Refinements</div>
                                    <ul className="mt-2 space-y-1">
                                        {current.suggestions.map((s, i) => <li key={i} className="text-xs text-foreground">• {s}</li>)}
                                    </ul>
                                </div>
                            )}
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
}
